<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcAttachmentsPanel',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'stage', 'unstage']);

  const props = defineProps({
    parent_id: {
      type: String,
      required: false,
    },
    parent: {
      type: Object,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    layout: {
      type: String,
      required: false,
      default: 'list',
    },
    pending: {
      type: Array,
      required: false,
      default: [],
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const container = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    container: container.value,
    ux: ux.value,
  }));

  const $$load_container = async () => {
    return props.parent_id || (props.parent && props.parent.id)
      ? $fn.value.fetch('list_attachment', {
          parameters: { 'owner-id': props.parent_id || props.parent.id },
          asResourceContainer: true,
        })
      : { data: [] };
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'container' }, { name: 'ux' }],
    loaders: { container: $$load_container },
    ux: {},
    dataRefs: { container, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const ownerId = computed(() => {
    return props.parent_id || (props.parent && props.parent.id);
  });
  const items = computed(() => {
    if (loaded.value) {
      return container.value.data;
    }
    return null;
  });
  const isReadOnly = computed(() => {
    return props.readonly || (props.parent && props.parent.isReadOnly);
  });
  const hasAny = computed(() => {
    return (
      (items.value && items.value.length) ||
      (props.pending && props.pending.length)
    );
  });

  watch(
    () => props.parent_id,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parent_id');
    }
  );
  watch(
    () => props.parent,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parent');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );
  watch(
    () => props.layout,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('layout');
    }
  );
  watch(
    () => props.pending,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('pending');
    }
  );

  const act_5382094220310827_showModal_ojkn77uhi_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'WAttachmentModal',
      props: {
        object_id: ownerId.value,
        attachment: _.cloneDeep($event.scope.item),
      },
    };
  };
  const act_2688566256707306_request_1pj69o5nj_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_attachment',
      data: $event.scope.item,
    };
  };
  const act_2688566256707306_request_1pj69o5nj_getConfirm = async ($event) => {
    return {
      message: $fn.value.gettext('Please confirm delete.'),
      btnOk: $fn.value.gettext('Delete'),
    };
  };
  const act_5962862420439144_showModal_pz64kb5v0_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'WAttachmentModal',
      props: {
        object_id: ownerId.value,
      },
    };
  };
  const act_96700093353179_showModal_jrntp2n6x_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'WAttachmentModal',
      props: {
        object_id: ownerId.value,
      },
    };
  };
  const act_5974459770896956_emit_iub7ecwyw_getActionArgs = async ($event) => {
    return {
      event: 'stage',
      value: $event.data.modalResult.value,
    };
  };
  const inst_evh_5974459770896956_close_2hywlcmil_getWhen = async ($event) => {
    return $event.data.modalResult && $event.data.modalResult.value;
  };
  const act_5974459770896956_showModal_vru9cyz62_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'WAttachmentModal',
      props: {
        defer: true,
      },
    };
  };
  const act_3494165723496827_showModal_yze6qfya4_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'WAttachmentModal',
      props: {
        object_id: ownerId.value,
        attachment: _.cloneDeep($event.scope.item),
      },
    };
  };
  const act_4108270090928914_request_x01qde7n0_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_attachment',
      data: $event.scope.item,
    };
  };
  const act_4108270090928914_request_x01qde7n0_getConfirm = async ($event) => {
    return {
      message: $fn.value.gettext('Please confirm delete.'),
      btnOk: $fn.value.gettext('Delete'),
    };
  };
  const act_7119126147772776_emit_55k36iytn_getActionArgs = async ($event) => {
    return {
      event: 'unstage',
      value: $event.scope.p._uid,
    };
  };

  const act_5382094220310827_reloadComponentData_tdtxsdft5 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_5382094220310827_close_vbi9kvod6 =
    new actionTypes.EventHandlerImpl({
      action: act_5382094220310827_reloadComponentData_tdtxsdft5,
      event: 'close',
      displayName: 'reloadComponentData',
    });
  const act_5382094220310827_showModal_ojkn77uhi =
    new actionTypes.ShowModalAction({
      actionArgs: act_5382094220310827_showModal_ojkn77uhi_getActionArgs,
      events: [inst_evh_5382094220310827_close_vbi9kvod6],
    });
  const inst_evh_5382094220310827_clickPreventStop_5x529k6ip =
    new actionTypes.EventHandlerImpl({
      action: act_5382094220310827_showModal_ojkn77uhi,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_5382094220310827_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5382094220310827_clickPreventStop_5x529k6ip],
      event: 'click.prevent.stop',
    });
  const act_2688566256707306_reloadComponentData_d1dvr3uek =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2688566256707306_success_dnhqny6jz =
    new actionTypes.EventHandlerImpl({
      action: act_2688566256707306_reloadComponentData_d1dvr3uek,
      event: 'success',
      displayName: 'reloadComponentData',
    });
  const act_2688566256707306_request_1pj69o5nj = new actionTypes.RequestAction({
    actionArgs: act_2688566256707306_request_1pj69o5nj_getActionArgs,
    confirm: act_2688566256707306_request_1pj69o5nj_getConfirm,
    events: [inst_evh_2688566256707306_success_dnhqny6jz],
  });
  const inst_evh_2688566256707306_clickPreventStop_9gggbddrl =
    new actionTypes.EventHandlerImpl({
      action: act_2688566256707306_request_1pj69o5nj,
      event: 'click.prevent.stop',
      displayName: 'request',
    });
  const inst_evh_2688566256707306_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2688566256707306_clickPreventStop_9gggbddrl],
      event: 'click.prevent.stop',
    });
  const act_5962862420439144_reloadComponentData_e9juahy43 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_5962862420439144_close_rrm5g2n95 =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_reloadComponentData_e9juahy43,
      event: 'close',
      displayName: 'reloadComponentData',
    });
  const act_5962862420439144_showModal_pz64kb5v0 =
    new actionTypes.ShowModalAction({
      actionArgs: act_5962862420439144_showModal_pz64kb5v0_getActionArgs,
      events: [inst_evh_5962862420439144_close_rrm5g2n95],
    });
  const inst_evh_5962862420439144_clickPreventStop_3tffihf56 =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_showModal_pz64kb5v0,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_5962862420439144_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_clickPreventStop_3tffihf56],
      event: 'click.prevent.stop',
    });
  const act_96700093353179_reloadComponentData_levyq6bb1 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_96700093353179_close_esn713tdl =
    new actionTypes.EventHandlerImpl({
      action: act_96700093353179_reloadComponentData_levyq6bb1,
      event: 'close',
      displayName: 'reloadComponentData',
    });
  const act_96700093353179_showModal_jrntp2n6x =
    new actionTypes.ShowModalAction({
      actionArgs: act_96700093353179_showModal_jrntp2n6x_getActionArgs,
      events: [inst_evh_96700093353179_close_esn713tdl],
    });
  const inst_evh_96700093353179_clickPreventStop_uqnpqagrm =
    new actionTypes.EventHandlerImpl({
      action: act_96700093353179_showModal_jrntp2n6x,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_96700093353179_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_96700093353179_clickPreventStop_uqnpqagrm],
      event: 'click.prevent.stop',
    });
  const act_5974459770896956_emit_iub7ecwyw = new actionTypes.EmitAction({
    actionArgs: act_5974459770896956_emit_iub7ecwyw_getActionArgs,
  });
  const inst_evh_5974459770896956_close_2hywlcmil =
    new actionTypes.EventHandlerImpl({
      action: act_5974459770896956_emit_iub7ecwyw,
      event: 'close',
      when: inst_evh_5974459770896956_close_2hywlcmil_getWhen,
      displayName: 'emit',
    });
  const act_5974459770896956_showModal_vru9cyz62 =
    new actionTypes.ShowModalAction({
      actionArgs: act_5974459770896956_showModal_vru9cyz62_getActionArgs,
      events: [inst_evh_5974459770896956_close_2hywlcmil],
    });
  const inst_evh_5974459770896956_clickPreventStop_lhwkpysvk =
    new actionTypes.EventHandlerImpl({
      action: act_5974459770896956_showModal_vru9cyz62,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_5974459770896956_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5974459770896956_clickPreventStop_lhwkpysvk],
      event: 'click.prevent.stop',
    });
  const act_3494165723496827_reloadComponentData_4kqoa7tyb =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_3494165723496827_close_q8wczqg3d =
    new actionTypes.EventHandlerImpl({
      action: act_3494165723496827_reloadComponentData_4kqoa7tyb,
      event: 'close',
      displayName: 'reloadComponentData',
    });
  const act_3494165723496827_showModal_yze6qfya4 =
    new actionTypes.ShowModalAction({
      actionArgs: act_3494165723496827_showModal_yze6qfya4_getActionArgs,
      events: [inst_evh_3494165723496827_close_q8wczqg3d],
    });
  const inst_evh_3494165723496827_clickPreventStop_alro0l7k0 =
    new actionTypes.EventHandlerImpl({
      action: act_3494165723496827_showModal_yze6qfya4,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_3494165723496827_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3494165723496827_clickPreventStop_alro0l7k0],
      event: 'click.prevent.stop',
    });
  const act_4108270090928914_reloadComponentData_68r1f4tn8 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_4108270090928914_success_qjte7bjwc =
    new actionTypes.EventHandlerImpl({
      action: act_4108270090928914_reloadComponentData_68r1f4tn8,
      event: 'success',
      displayName: 'reloadComponentData',
    });
  const act_4108270090928914_request_x01qde7n0 = new actionTypes.RequestAction({
    actionArgs: act_4108270090928914_request_x01qde7n0_getActionArgs,
    confirm: act_4108270090928914_request_x01qde7n0_getConfirm,
    events: [inst_evh_4108270090928914_success_qjte7bjwc],
  });
  const inst_evh_4108270090928914_clickPreventStop_467yvzrcr =
    new actionTypes.EventHandlerImpl({
      action: act_4108270090928914_request_x01qde7n0,
      event: 'click.prevent.stop',
      displayName: 'request',
    });
  const inst_evh_4108270090928914_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4108270090928914_clickPreventStop_467yvzrcr],
      event: 'click.prevent.stop',
    });
  const act_7119126147772776_emit_55k36iytn = new actionTypes.EmitAction({
    actionArgs: act_7119126147772776_emit_55k36iytn_getActionArgs,
  });
  const inst_evh_7119126147772776_clickPreventStop_p3n1wjkmt =
    new actionTypes.EventHandlerImpl({
      action: act_7119126147772776_emit_55k36iytn,
      event: 'click.prevent.stop',
      displayName: 'emit',
    });
  const inst_evh_7119126147772776_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7119126147772776_clickPreventStop_p3n1wjkmt],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_5382094220310827_close_vbi9kvod6,
    inst_evh_5382094220310827_clickPreventStop_5x529k6ip,
    inst_evh_5382094220310827_clickPreventStop,
    inst_evh_2688566256707306_success_dnhqny6jz,
    inst_evh_2688566256707306_clickPreventStop_9gggbddrl,
    inst_evh_2688566256707306_clickPreventStop,
    inst_evh_5962862420439144_close_rrm5g2n95,
    inst_evh_5962862420439144_clickPreventStop_3tffihf56,
    inst_evh_5962862420439144_clickPreventStop,
    inst_evh_96700093353179_close_esn713tdl,
    inst_evh_96700093353179_clickPreventStop_uqnpqagrm,
    inst_evh_96700093353179_clickPreventStop,
    inst_evh_5974459770896956_close_2hywlcmil,
    inst_evh_5974459770896956_clickPreventStop_lhwkpysvk,
    inst_evh_5974459770896956_clickPreventStop,
    inst_evh_3494165723496827_close_q8wczqg3d,
    inst_evh_3494165723496827_clickPreventStop_alro0l7k0,
    inst_evh_3494165723496827_clickPreventStop,
    inst_evh_4108270090928914_success_qjte7bjwc,
    inst_evh_4108270090928914_clickPreventStop_467yvzrcr,
    inst_evh_4108270090928914_clickPreventStop,
    inst_evh_7119126147772776_clickPreventStop_p3n1wjkmt,
    inst_evh_7119126147772776_clickPreventStop,
  };

  const evh_5382094220310827_close_vbi9kvod6 = async (event, scope) => {
    return await actions.inst_evh_5382094220310827_close_vbi9kvod6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5382094220310827_clickPreventStop_5x529k6ip = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5382094220310827_clickPreventStop_5x529k6ip.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5382094220310827_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_5382094220310827_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2688566256707306_success_dnhqny6jz = async (event, scope) => {
    return await actions.inst_evh_2688566256707306_success_dnhqny6jz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2688566256707306_clickPreventStop_9gggbddrl = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2688566256707306_clickPreventStop_9gggbddrl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2688566256707306_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2688566256707306_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_close_rrm5g2n95 = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_close_rrm5g2n95.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_clickPreventStop_3tffihf56 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_clickPreventStop_3tffihf56.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_96700093353179_close_esn713tdl = async (event, scope) => {
    return await actions.inst_evh_96700093353179_close_esn713tdl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_96700093353179_clickPreventStop_uqnpqagrm = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_96700093353179_clickPreventStop_uqnpqagrm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_96700093353179_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_96700093353179_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5974459770896956_close_2hywlcmil = async (event, scope) => {
    return await actions.inst_evh_5974459770896956_close_2hywlcmil.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5974459770896956_clickPreventStop_lhwkpysvk = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5974459770896956_clickPreventStop_lhwkpysvk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5974459770896956_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_5974459770896956_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3494165723496827_close_q8wczqg3d = async (event, scope) => {
    return await actions.inst_evh_3494165723496827_close_q8wczqg3d.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3494165723496827_clickPreventStop_alro0l7k0 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3494165723496827_clickPreventStop_alro0l7k0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3494165723496827_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3494165723496827_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4108270090928914_success_qjte7bjwc = async (event, scope) => {
    return await actions.inst_evh_4108270090928914_success_qjte7bjwc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4108270090928914_clickPreventStop_467yvzrcr = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4108270090928914_clickPreventStop_467yvzrcr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4108270090928914_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4108270090928914_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7119126147772776_clickPreventStop_p3n1wjkmt = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7119126147772776_clickPreventStop_p3n1wjkmt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7119126147772776_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7119126147772776_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox direction="column" v-if="layout !== 'wrap'">
    <ItpBox direction="column" v-if="items && items.length">
      <ItpBox
        :key="item.id"
        alignItems="center"
        class="border rounded rounded-3 p-2 mb-2 overflow-hidden"
        v-for="item in items"
      >
        <UcFileIcon size="small" :filename="item.file_name" class="me-2">
        </UcFileIcon>
        <ItpLink
          :text="item.file_name"
          :href="item.download_url"
          :tooltip="item.file_name"
          :downloadFileName="item.file_name"
          download-link-only
          use-auth
        >
        </ItpLink>
        <BDropdown
          boundary="viewport"
          size="sm"
          toggle-class="btn-default-borderless"
          no-caret
          variant="none"
          dropleft
          class="ms-auto mb-auto"
          v-if="!item.locked"
        >
          <template #button-content>
            <ItpIcon fa="ellipsis-v" class="mx-1"> </ItpIcon>
          </template>
          <BDropdownItemButton
            @click.prevent.stop="
              evh_5382094220310827_clickPreventStop($event, { item })
            "
          >
            <ItpText :text="pgettext('Command', 'Edit')"> </ItpText>
          </BDropdownItemButton>
          <BDropdownItemButton
            @click.prevent.stop="
              evh_2688566256707306_clickPreventStop($event, { item })
            "
          >
            <ItpText :text="pgettext('Command', 'Delete')"> </ItpText>
          </BDropdownItemButton>
        </BDropdown>
      </ItpBox>
    </ItpBox>
    <ItpLink
      :icon="$config.addIcon"
      :text="pgettext('Attachments', 'Upload file...')"
      class="ms-auto"
      v-if="!isReadOnly"
      @click.prevent.stop="evh_5962862420439144_clickPreventStop($event, {})"
    >
    </ItpLink>
  </ItpBox>
  <div v-if="layout === 'wrap'">
    <ItpLink
      :icon="$config.addIcon"
      :text="pgettext('Attachments', 'Upload file...')"
      class="itp-attachment-panel__upload d-inline-block"
      v-if="!isReadOnly && ownerId"
      @click.prevent.stop="evh_96700093353179_clickPreventStop($event, {})"
    >
    </ItpLink>
    <ItpLink
      :icon="$config.addIcon"
      :text="pgettext('Attachments', 'Upload file...')"
      class="itp-attachment-panel__upload d-inline-block"
      v-if="!isReadOnly && !ownerId"
      @click.prevent.stop="evh_5974459770896956_clickPreventStop($event, {})"
    >
    </ItpLink>
    <div class="itp-attachment-grid mt-2" v-if="hasAny">
      <div
        :key="item.id"
        class="itp-attachment-box border rounded-2 px-2 py-1 d-flex align-items-center gap-2"
        v-for="item in _.orderBy(items, ['created_at'], ['desc'])"
      >
        <UcFileIcon
          size="small"
          :filename="item.file_name"
          class="flex-shrink-0"
        >
        </UcFileIcon>
        <ItpLink
          :text="item.file_name"
          :href="item.download_url"
          :tooltip="item.file_name"
          :downloadFileName="item.file_name"
          download-link-only
          use-auth
          class="itp-attachment-box__name"
        >
        </ItpLink>
        <ItpText class="text-muted small text-nowrap flex-shrink-0">
          {{ filesize(item.file_size) }}
        </ItpText>
        <BDropdown
          boundary="viewport"
          size="sm"
          toggle-class="btn-default-borderless"
          no-caret
          variant="none"
          dropleft
          class="flex-shrink-0"
          v-if="!item.locked && !isReadOnly"
        >
          <template #button-content>
            <ItpIcon fa="ellipsis-v" class="mx-1"> </ItpIcon>
          </template>
          <BDropdownItemButton
            @click.prevent.stop="
              evh_3494165723496827_clickPreventStop($event, { item })
            "
          >
            <ItpText :text="pgettext('Command', 'Edit')"> </ItpText>
          </BDropdownItemButton>
          <BDropdownItemButton
            @click.prevent.stop="
              evh_4108270090928914_clickPreventStop($event, { item })
            "
          >
            <ItpText :text="pgettext('Command', 'Delete')"> </ItpText>
          </BDropdownItemButton>
        </BDropdown>
      </div>
      <div
        :key="p._uid"
        class="itp-attachment-box itp-attachment-box--pending border rounded-2 px-2 py-1 d-flex align-items-center gap-2 bg-body-secondary"
        v-for="p in pending"
      >
        <UcFileIcon size="small" :filename="p.file.name" class="flex-shrink-0">
        </UcFileIcon>
        <span class="itp-attachment-box__name">
          {{ p.file.name }}
        </span>
        <ItpText class="text-muted small text-nowrap flex-shrink-0">
          {{ filesize(p.file.size) }}
        </ItpText>
        <ItpButton
          size="sm"
          variant="light"
          icon="fa-times"
          :tooltip="pgettext('Command', 'Remove')"
          class="flex-shrink-0"
          v-if="!isReadOnly"
          @click.prevent.stop="
            evh_7119126147772776_clickPreventStop($event, { p })
          "
        >
        </ItpButton>
      </div>
    </div>
  </div>
</template>
